import { useState, useEffect, useCallback } from 'react';
import { clientInvoicesService } from '../lib/database';
import { getDataSource } from '../lib/config';
import { writeClientAudit } from '../lib/clientAudit';
import { invoiceAuditSummary } from '../lib/moduleAudit';
import type { ClientInvoice } from '../types';

interface UseClientInvoicesReturn {
  clientInvoices: ClientInvoice[];
  loading: boolean;
  error: string | null;
  create: (invoice: Omit<ClientInvoice, 'id'>) => Promise<ClientInvoice>;
  update: (id: string, updates: Partial<ClientInvoice>) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

export const useClientInvoices = (enabled = true): UseClientInvoicesReturn => {
  const [clientInvoices, setClientInvoices] = useState<ClientInvoice[]>([]);
  const [loading, setLoading] = useState(() => enabled && getDataSource() !== 'mock');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (getDataSource() === 'mock') {
      setClientInvoices([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = clientInvoicesService.subscribe(
      (invoices) => {
        setClientInvoices(invoices);
        setLoading(false);
      },
      (err) => {
        setError(err instanceof Error ? err.message : 'Failed to load client invoices');
        setLoading(false);
      },
    );
    return () => unsub();
  }, [enabled]);

  const create = useCallback(async (invoice: Omit<ClientInvoice, 'id'>) => {
    const created = await clientInvoicesService.create(invoice);
    setClientInvoices((prev) => [created, ...prev.filter((i) => i.id !== created.id)]);
    writeClientAudit({
      action: 'client_invoice.added',
      entityType: 'client_invoice',
      entityId: created.id,
      summary: invoiceAuditSummary('Client invoice', created.invoiceNumber || created.id, undefined, created, true),
    });
    return created;
  }, []);

  const update = useCallback(async (id: string, updates: Partial<ClientInvoice>) => {
    const previous = clientInvoices.find((invoice) => invoice.id === id);
    await clientInvoicesService.update(id, updates);
    setClientInvoices((prev) =>
      prev.map((inv) => (inv.id === id ? { ...inv, ...updates } : inv)),
    );
    writeClientAudit({
      action: 'client_invoice.updated',
      entityType: 'client_invoice',
      entityId: id,
      summary: invoiceAuditSummary('Client invoice', updates.invoiceNumber || previous?.invoiceNumber || id, previous, { ...previous, ...updates }, false),
    });
  }, [clientInvoices]);

  const remove = useCallback(async (id: string) => {
    const previous = clientInvoices.find((invoice) => invoice.id === id);
    await clientInvoicesService.delete(id);
    setClientInvoices((prev) => prev.filter((inv) => inv.id !== id));
    writeClientAudit({
      action: 'client_invoice.removed',
      entityType: 'client_invoice',
      entityId: id,
      summary: `Client invoice ${previous?.invoiceNumber || id} removed`,
    });
  }, [clientInvoices]);

  return { clientInvoices, loading, error, create, update, remove };
};
